// Import the mixins
import { BugsnagService } from '@/mixins/services/bugsnag/BugsnagService'
import { SpinnerMethods } from '@/mixins/helpers/SpinnerMethods'
import { GenerateUID } from '@/mixins/helpers/GenerateUID'

// Import the repository
import { InvoicesRepository } from '@/repositories/invoices/InvoicesRepository'
import { InvoiceEventsRepository } from '@/repositories/invoice-events/InvoiceEventsRepository'

// Import firebase
import { propertiesModel } from '@/firebase/firebase-config'

export const CreateInvoice = {

    /*
	**  Register the mixins
	*/

	mixins: [

        BugsnagService,
        SpinnerMethods,
        InvoicesRepository,
        InvoiceEventsRepository,
        GenerateUID

    ],

    /*
	**  Set up the data
	*/

	data: () => ({

        error: false,
        totalRemaining: 0,
        totalCreated: 0

    }),

    /*
	**  Methods
    */
    
    methods: {

        createInvoiceHandler: function() {

            /*
            **  Show the spinner
            **  @method: Mixins/Helpers/SpinnerMethods
            */

            this.showSpinner();

            /*
            **  Set the relationships
            **  @method: @here
            */

			this.setNewInvoiceRelationships()
            .then(() => {

                /* 
                **  Set the amounts
                **  @method: @here
                */

                if (!this.error) this.setNewInvoiceAmounts();

            })
            .then(() => {

                /* 
                **  Set the card fees
                **  @method: @here
                */

                if (!this.error) this.setNewInvoiceCardFees();

            })
            .then(() => {

                /* 
                **  Set the ach fees
                **  @method: @here
                */

                if (!this.error) this.setNewInvoiceACHFees();

            })
            .then(() => {

                /* 
                **  Set the transaction fee line item
                **  @method: @here
                */

                if (!this.error && this.rent.attributes.transaction_fees_policy.method_type == 'resident') this.setNewInvoiceTransactionFee();

            })
            .then(() => {

                /* 
                **  Set the event attributes
                **  @method: @here
                */

                if (!this.error) this.setNewInvoiceEventAttributes();

            })
            .then(() => {

                /* 
                **  Convert the dates
                **  @method: @here
                */

                if (!this.error) this.convertNewInvoiceDates();

            })
            .then(() => {

                /* 
                **  Generate the UID for the new invoice
                **  @method: @here
                */

                if (!this.error) this.setNewInvoiceUID();

            })
            .then(() => {

                /* 
                **  Create the rent invoice
                **  @method: @here
                */

                if (!this.error) this.createNewInvoice();

            })
            .then(() => {

                /* 
                **  Set the rent metrics
                **  @method: @here
                */

                if (!this.error) this.setNewInvoiceRentMetrics();

            })
            .then(() => {

                /* 
                **  Update rent metrics
                **  @method: @here
                */

                if (!this.error) this.updateNewInvoiceRentMetrics();

            })
            .then(() => {

                /* 
                **  Create initial invoice event
                **  @method: @here
                */

                if (!this.error) this.createNewInvoiceFirstEvent();

            })
            .then(() => {

                /* 
                **  Reset the invoice event
                **  @method: @here
                */

                if (!this.error) this.resetNewInvoiceEvent();

            })
            .then(() => {

                /* 
                ** Finish creating the invoice
                ** @method: @here
                */

                if (!this.error) this.finishCreateNewInvoice();

            })
            .then(() => {

                /* 
                ** If error, handle it
                ** @method: @here
                */

                if (this.error) this.handleNewInvoiceError();

            });

        },

        handleNewInvoiceError: function() {

            /* 
            **  Reset the checks and balances
            **  @method: Components/Property/Lease/Invoices/CreateInvoiceMenu
            */

            this.resetChecksProps();

            /* 
            **  Close the invoice menu
            **  @method: Components/Property/Lease/Invoices/CreateInvoiceMenu
            */

            this.closeNewInvoiceMenu();
            
            /* 
            **  Reset the processing prop
            **  @prop: Components/Property/Lease/Invoices/CreateInvoiceMenu
            */

            this.processing = false;

            /* 
            **  Hide the spinner
            **  @method: Mixins/Helpers/SpinnerMethods
            */

            this.hideSpinner();

            /* 
            **  Throw error message
            **  @service: SweetAlert2
            */

            this.$swal('Uh Oh!', 'We ran into some trouble while trying to create your invoice.', 'error');

        },

        setNewInvoiceRelationships: async function() {

            try {

                /* 
                **  Set the invoice relationship props
                **  @prop: Repositories/Invoices/InvoiceRepository
                */

                this.new_rent_invoice.relationships.lease = this.lease.id;
                this.new_rent_invoice.relationships.property = this.property.id;
                this.new_rent_invoice.relationships.resident = this.resident.id;
                this.new_rent_invoice.relationships.unit = this.unit.id;
                this.new_rent_invoice.relationships.company = this.company.id;
                this.new_rent_invoice.relationships.rent = this.rent.id;
                
                /* 
                **  Set invoice event relationships
                **  @prop: Repositories/Invoice-Events/InvoiceEventsRepository
                */

                this.new_invoice_event.relationships.lease = this.lease.id;
                this.new_invoice_event.relationships.property = this.property.id;
                this.new_invoice_event.relationships.resident = this.resident.id;
                this.new_invoice_event.relationships.unit = this.unit.id;
                this.new_invoice_event.relationships.company = this.company.id;
                this.new_invoice_event.relationships.rent = this.rent.id;

            } catch (e) {

                /*
                **  Set the error prop
                **  @prop: @here
                */

                this.error = true;

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */
                
                this.reportAuthBugsnagError({
                    title: 'Setting invoice and invoice events props when creating a new invoice.',
                    errorType: 'Application',
                    section: 'Events/Invoices',
                    uid: 'EiEXChkEzJHRL7mAsYd1',
                    error: e
                });

            }

        },

        setNewInvoiceAmounts: function() {

            try {

                /* 
                **  Convert the amounts
                **  @prop: Repositories/Invoices/InvoicesRepository
                */

				this.new_rent_invoice.attributes.charge.amount = this.new_rent_invoice.attributes.charge.amount * 100;
				this.new_rent_invoice.attributes.proration.amount = this.new_rent_invoice.attributes.proration.amount * 100;
				this.new_rent_invoice.attributes.charge.policy_late_fee = this.new_rent_invoice.attributes.charge.policy_late_fee * 100;
 
                /* 
                **  Set invoice amount, if prorated
                **  @prop: Repositories/Invoices/InvoicesRepository
                */

                if (this.new_rent_invoice.attributes.proration.prorated) this.new_rent_invoice.attributes.charge.amount = this.new_rent_invoice.attributes.proration.amount;
 
                /* 
                **  Set the line item amounts
                **  @prop: Repositories/Invoices/InvoicesRepository
                */

                this.new_rent_invoice.attributes.charge.subtotal = this.new_rent_invoice.attributes.charge.amount + this.rent.attributes.transaction_fees_policy.resident.fee_amount;
                this.new_rent_invoice.attributes.charge.total = this.new_rent_invoice.attributes.charge.amount + this.rent.attributes.transaction_fees_policy.resident.fee_amount;

                /* 
                **  Set the line item details
                **  @prop: Repositories/Invoices/InvoicesRepository
                */

                this.new_rent_invoice.attributes.line_items[0].amount = this.new_rent_invoice.attributes.charge.amount;
				this.new_rent_invoice.attributes.line_items[0].description = moment(this.new_rent_invoice.attributes.due_on.date).format('MMMM') + ' ' + moment(this.new_rent_invoice.attributes.due_on.date).format('YYYY') + ' Rent';
				
                /* 
                **  Set the transaction fee
                **  @prop: Repositories/Invoices/InvoicesRepository
                */

				this.new_rent_invoice.attributes.charge.transaction_fee = this.rent.attributes.transaction_fees_policy.resident.fee_amount;

                /* 
                **  Set the notification days in advance
                **  @prop: Repositories/Invoices/InvoicesRepository
                */

				this.new_rent_invoice.attributes.notification.days_in_advance = this.rent.attributes.due_notification.days_in_advance;

            } catch (e) {

                /*
                **  Set the error prop
                **  @prop: @here
                */

                this.error = true;

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */
                
                this.reportAuthBugsnagError({
                    title: 'Setting invoice amounts when creating a new invoice.',
                    errorType: 'Application',
                    section: 'Events/Invoices',
                    uid: 'OBnkrbazjmivihKQdiYL',
                    error: e
                });

            }

        },

        setNewInvoiceCardFees: function() {

            try {

                /*
                **  Set the total amount
                */

				let total = this.new_rent_invoice.attributes.charge.total;
 
                /*
                **  Calculate the Stripe variables
                */

				let stripeCardPercent = Number(process.env.VUE_APP_STRIPE_CARD_FEE_PERCENT) + Number(process.env.VUE_APP_STRIPE_EXPRESS_ACCOUNT_FEE_PERCENT),
					stripeCardCents = Number(process.env.VUE_APP_STRIPE_CARD_FEE_CENTS) + Number(process.env.VUE_APP_STRIPE_EXPRESS_ACCOUNT_FEE_CENTS);
 
                /*
                **  Calculate platform fees
                */

				let platformFee = total * Number(process.env.VUE_APP_PLATFORM_FEE_PERCENT);
				platformFee = Math.ceil(platformFee);

                /*
                **  Calculate stripe fees
                */

				let stripeFee = (total * stripeCardPercent) + stripeCardCents;
				stripeFee = Math.ceil(stripeFee);

                /*
                **  Calculate the total fees in pennies
                */

				let totalFees = platformFee + stripeFee;
 
                /*
                **  Calculate connect account amount
                */

				let connectAccountAmount = total - totalFees;
 
                /*
                **  Set the props
                */

                this.new_rent_invoice.attributes.charge.card_fees.stripe.amount = Number(stripeFee);
                this.new_rent_invoice.attributes.charge.card_fees.platform.amount = Number(platformFee);
				this.new_rent_invoice.attributes.charge.card_fees.total_fees = Number(totalFees);
				this.new_rent_invoice.attributes.charge.card_fees.connect_account_amount = connectAccountAmount;

            } catch (e) {

                /*
                **  Set the error prop
                **  @prop: @here
                */

                this.error = true;

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */
                
                this.reportAuthBugsnagError({
                    title: 'Setting the new invoice card fees while creating an invoice.',
                    errorType: 'Application',
                    section: 'Events/Invoices',
                    uid: 'vA8vfCA9kklEtGWDj6LQ',
                    error: e
                });

            }

        },

        setNewInvoiceACHFees: function() {

            try {

                /*
                **  Set the total amount
                */

				let total = this.new_rent_invoice.attributes.charge.total;

				/*
                **  Calculate the Stripe variables
                */

				let stripeACHPercent =  Number(process.env.VUE_APP_STRIPE_ACH_FEE_PERCENT) + Number(process.env.VUE_APP_STRIPE_EXPRESS_ACCOUNT_FEE_PERCENT),
					stripeACHCents = Number(process.env.VUE_APP_STRIPE_EXPRESS_ACCOUNT_FEE_CENTS);
 
                /*
                **  Calculate platform fees
                */

				let platformFee = total * Number(process.env.VUE_APP_PLATFORM_FEE_PERCENT);
				platformFee = Math.ceil(platformFee);
 
                /*
                **  Calculate stripe fees
                */

				let stripeFee = (total * stripeACHPercent) + stripeACHCents;
				stripeFee = Math.ceil(stripeFee);
 
                /*
                **  Calculate the total fees in pennies
                */

				let totalFees = platformFee + stripeFee;

                /*
                **  Calculate connect account amount
                */

				let connectAccountAmount = total - totalFees;
 
                /*
                **  Set the fees
                */

                this.new_rent_invoice.attributes.charge.ach_fees.stripe.amount = Number(stripeFee);
                this.new_rent_invoice.attributes.charge.ach_fees.platform.amount = Number(platformFee);
				this.new_rent_invoice.attributes.charge.ach_fees.total_fees = Number(totalFees);
				this.new_rent_invoice.attributes.charge.ach_fees.connect_account_amount = connectAccountAmount;

            } catch (e) {

                /*
                **  Set the error prop
                **  @prop: @here
                */

                this.error = true;

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */
                
                this.reportAuthBugsnagError({
                    title: 'Setting the new invoice ACH fees while creating an invoice.',
                    errorType: 'Application',
                    section: 'Events/Invoices',
                    uid: 'Uxw9dMUS7bE4QeaOBvdN',
                    error: e
                });

            }

        },

        setNewInvoiceTransactionFee: function() {

            try {

                /*
                **  Set the new transaction fee line item
                **  @prop: Repositories/Invoices/InvoicesRepository
                */

                this.new_rent_invoice.attributes.line_items.push({
					amount: this.rent.attributes.transaction_fees_policy.resident.fee_amount,
					type: 'transaction_fee',
					description: 'Electronic Payments Transaction Fee',
					paid: false,
					paid_amount: 0
				});

            } catch (e) {

                /*
                **  Set the error prop
                **  @prop: @here
                */

                this.error = true;

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */
                
                this.reportAuthBugsnagError({
                    title: 'Setting the new transaction fee line item while creating an invoice.',
                    errorType: 'Application',
                    section: 'Events/Invoices',
                    uid: '3pTNs4PEjQlOQA4qz3o2',
                    error: e
                });

            }

        },

        setNewInvoiceEventAttributes: function() {

            /*
            **  Set the invoice event attributes
            **  @prop: Repositories/Invoice-Events/InvoiceEventsRepository
            */

            try {

                this.new_invoice_event.id = this.generateUID();
                this.new_invoice_event.type = 'invoice.create';
                this.new_invoice_event.attributes.title = 'A draft invoice was created for ' + this.resident.attributes.contact_information.first_name + ' ' + this.resident.attributes.contact_information.last_name + ' and ' + this.unit.attributes.address.street_1 + ', Unit ' + this.unit.attributes.number + '.';
                this.new_invoice_event.attributes.application = 'Nestd | Web';
                this.new_invoice_event.attributes.source = 'Nestd - ' + this.user.attributes.profile.email;
                this.new_invoice_event.attributes.response = this.new_rent_invoice;
                this.new_invoice_event.attributes.request = {
                    status: this.new_rent_invoice.attributes.status,
                    proration: {
                        prorated: this.new_rent_invoice.attributes.proration.prorated,
                        amount: this.new_rent_invoice.attributes.proration.amount
                    },
                    due_on: {
                        date: moment(this.new_rent_invoice.attributes.due_on.date).unix()
                    },
                    late_policy: {
                        date: moment(this.new_rent_invoice.attributes.late_policy.date).unix()
                    },
                    notification: {
                        date: moment(this.new_rent_invoice.attributes.notification.date).unix(),
                        days_in_advance: this.new_rent_invoice.attributes.notification.days_in_advance
                    },
                    description: this.new_rent_invoice.attributes.description
                };

            } catch (e) {

                /*
                **  Set the error prop
                **  @prop: @here
                */

                this.error = true;

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */
                
                this.reportAuthBugsnagError({
                    title: 'Setting the invoice events attributes while creating an invoice.',
                    errorType: 'Application',
                    section: 'Events/Invoices',
                    uid: '8zF44cDu4dlZHmSlop9x',
                    error: e
                });

            }

        },

        convertNewInvoiceDates: function() {

            try {

                // Convert the dates
			    this.new_rent_invoice.attributes.due_on.date = moment(this.new_rent_invoice.attributes.due_on.date).unix();
			    this.new_rent_invoice.attributes.notification.date = moment(this.new_rent_invoice.attributes.notification.date, 'YYYY-MM-DD').unix();
			    this.new_rent_invoice.attributes.late_policy.date = moment(this.new_rent_invoice.attributes.late_policy.date, 'YYYY-MM-DD').unix();

            } catch (e) {

                /*
                **  Set the error prop
                **  @prop: @here
                */

                this.error = true;

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */
                
                this.reportAuthBugsnagError({
                    title: 'Converting the dates while creating an invoice.',
                    errorType: 'Application',
                    section: 'Events/Invoices',
                    uid: 'RGOYdEZu5qU6HFLzGVT8',
                    error: e
                });

            }

        },

        setNewInvoiceUID: function() {

            try {

                /*
                **  Generate UID
                **  @prop: Repositories/Invoices/InvoicesRepository
                */

			    this.new_rent_invoice.id = this.generateUID();

            } catch (e) {

                /*
                **  Set the error prop
                **  @prop: @here
                */

                this.error = true;

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */
                
                this.reportAuthBugsnagError({
                    title: 'Setting the new invoice UID while creating an invoice.',
                    errorType: 'Application',
                    section: 'Events/Invoices',
                    uid: 'n0uEX3oivA4atEl84trA',
                    error: e
                });

            }

        },

        createNewInvoice: function() {

            /*
            **  Create doc in database
            **  @service: Firebase/Firestore
            */

			propertiesModel.doc(this.$route.params.id).collection('leases').doc(this.lease.id).collection('rents').doc(this.rent.id).collection('invoices').doc(this.new_rent_invoice.id).set(this.new_rent_invoice)
            .catch(error => {

                /*
                **  Set the error prop
                **  @prop: @here
                */

                this.error = true;

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */
                
                this.reportAuthBugsnagError({
                    title: 'Setting the new invoice UID while creating an invoice.',
                    errorType: 'Application',
                    section: 'Events/Invoices',
                    uid: 'ZIm03y8tTUIaqedakZ2x',
                    error: error
                });

            });

        },

        setNewInvoiceRentMetrics: function() {

            try {

                /*
                **  Calculate metrics
                */

                this.totalCreated = this.rent.metrics.total_created_invoices + 1;
                this.totalRemaining = this.totalCreated;

                /*
                **  Check if any payments were made
                */

                if (this.rent.metrics.total_charges > 0) this.totalRemaining = this.rent.metrics.total_charges - this.totalCreated;

            } catch (e) {

                /*
                **  Set the error prop
                **  @prop: @here
                */

                this.error = true;

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */
                
                this.reportAuthBugsnagError({
                    title: 'Setting the new invoice UID while creating an invoice.',
                    errorType: 'Application',
                    section: 'Events/Invoices',
                    uid: 'mAKdNHUAM70HodHZFeS4',
                    error: e
                });

            }

        },

        updateNewInvoiceRentMetrics: function() {

            /*
            **  Update the rent metrics
            **  @service: Firebase/Firestore
            */

            propertiesModel.doc(this.$route.params.id).collection('leases').doc(this.lease.id).collection('rents').doc(this.rent.id).update({

                'metrics.total_remaining': this.totalRemaining,
                'metrics.total_created_invoices': this.totalCreated,
                'meta.updated_at': moment().unix()

            })
            .catch(error => {

                /*
                **  Set the error prop
                **  @prop: @here
                */

                this.error = true;

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */

                this.reportAuthBugsnagError({
                    title: 'Update Rent - Update Total Remaining Metric in Firestore',
                    errorType: 'Application',
                    section: 'Events/Invoices',
                    uid: 'IUz6mQsrDVozbNBqZtHG',
                    error: error
                });

            });

        },

        createNewInvoiceFirstEvent: function() {

            /*
            **  Set the invoice relationship
            **  @prop: Repositories/Invoice-Events/InvoiceEventsRepository
            */

            this.new_invoice_event.relationships.invoice = this.new_rent_invoice.id;

            /*
            **  Create the invoice event
            **  @service: Firebase/Firestore
            */

            propertiesModel.doc(this.$route.params.id).collection('leases').doc(this.lease.id).collection('rents').doc(this.rent.id).collection('invoices').doc(this.new_rent_invoice.id).collection('events').doc(this.new_invoice_event.id).set(this.new_invoice_event)
            .catch(error => {

                /*
                **  Set the error prop
                **  @prop: @here
                */

                this.error = true;

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */

				this.reportAuthBugsnagError({
					title: 'Create New Rent Invoice Event in Firestore',
					errorType: 'Application',
					section: 'Events/Invoices',
                    uid: '1GIbQCMANrOjJe4gY3Qf',
					error: error
				});
                
            });

            /*
            **  Create the resident event
            **  @service: Firebase/Firestore
            */

            propertiesModel.doc(this.$route.params.id).collection('residents').doc(this.resident.id).collection('events').doc(this.new_invoice_event.id).set(this.new_invoice_event)
            .catch(error => {

                /*
                **  Set the error prop
                **  @prop: @here
                */

                this.error = true;

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */

				this.reportAuthBugsnagError({
					title: 'Create New Resident Invoice Event in Firestore',
					errorType: 'Application',
					section: 'Events/Invoices',
                    uid: '6JZB0PD6NomJmEnnhz5C',
					error: error
				});
                
            });

        },

        finishCreateNewInvoice: function() {

            /* 
            ** Reset the checks and balances
            ** @method: Components/Property/Lease/Invoices/CreateInvoiceMenu
            */

            this.resetChecksProps();

            /* 
            ** Create an invoice event
            ** @method: Components/Property/Lease/Invoices/CreateInvoiceMenu
            */

            this.closeNewInvoiceMenu();
            
            /* 
            ** Reset the processing prop
            ** @prop: Components/Property/Lease/Invoices/CreateInvoiceMenu
            */

            this.processing = false;

            /* 
            ** Hide the spinner
            ** @method: Mixins/Helpers/SpinnerMethods
            */

            this.hideSpinner();

            /* 
            ** Throw success message
            ** @service: SweetAlert2
            */

            this.$swal('Success!', 'Your rent invoice has been created.', 'success');

        }

    }

}